import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { Button, Card, EmptyState, PhotoAttach, Sheet, Skeleton, StatusChip, SuccessBlock, Textarea, Timeline } from '@/ds'
import { NotAvailable } from '@/app/components/NotAvailable'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { dayMonthTime } from '@/lib/format'
import { useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { peticionesService } from '@/services/peticiones'
import type { GuestRequest } from '@/services/peticiones'
import { useSave } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink } from '../Pagos/shared'
import { useLang } from '../Reserva/shared'
import { HELP_ROUTES, requestTitle, STATUS_CHIP } from './shared'

/**
 * 8.8 · Seguimiento de una petición (Servicios.dc.html track, Bloque 6 §6f): estado con icono y texto, recibida → en
 * curso → resuelta con lo que ha dicho la propiedad, y añadir un comentario o una foto. Contra mock (D48).
 */
export function Peticion() {
  const { t } = useLang()
  const { id = '' } = useParams()
  const stay = useLoadedStay()
  const { go } = useAppNavigate()
  const [data, reload] = useAsync(`peticion:${stay.guestCode}:${id}`, (signal) => peticionesService.get(stay, id, signal))
  return (
    <main className="page">
      <div className="pagos__head">
        <BackLink to={HELP_ROUTES.home} label={t('v2.peticiones.nueva.volver')} />
        <h1>{t('v2.peticiones.seguimiento.titulo')}</h1>
        {data.kind === 'ready' && <p className="page__lead">{t('v2.peticiones.seguimiento.lead')}</p>}
      </div>
      {data.kind === 'loading' && (
        <div className="pagos__stack" aria-busy="true">
          <Skeleton height={112} />
          <Skeleton height={240} />
        </div>
      )}
      {data.kind === 'error' && (data.code === 'not_found' ? <NotFound onAction={() => go(HELP_ROUTES.home)} /> : <SectionError code={data.code} onRetry={reload} />)}
      {data.kind === 'unsupported' && <NotAvailable />}
      {data.kind === 'ready' && <Tracking initial={data.value} />}
    </main>
  )
}

function NotFound({ onAction }: { onAction: () => void }) {
  const { t } = useLang()
  return (
    <EmptyState icon="search" title={t('v2.peticiones.seguimiento.noEncontradaTitulo')} headingLevel={2} actionLabel={t('v2.peticiones.seguimiento.verAyuda')} onAction={onAction}>
      {t('v2.peticiones.seguimiento.noEncontradaTexto')}
    </EmptyState>
  )
}

function Tracking({ initial }: { initial: GuestRequest }) {
  const { t, lang } = useLang()
  const stay = useLoadedStay()
  // Tras comentar se muestra la petición que devuelve el servicio, sin volver a cargar la pantalla.
  const [request, setRequest] = useState(initial)
  const chip = STATUS_CHIP[request.status]
  const when = dayMonthTime(request.createdAt, lang)
  const meta = [request.number ? t('v2.peticiones.seguimiento.meta', { number: request.number, when }) : t('v2.peticiones.seguimiento.metaSinNumero', { when }), request.photoCount > 0 ? t('v2.peticiones.seguimiento.fotos', { count: request.photoCount }) : null].filter(Boolean).join(' · ')

  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [photos, setPhotos] = useState<{ id: string; file: File; url: string | undefined }[]>([])
  const [missing, setMissing] = useState(false)
  const [sent, setSent] = useState(false)
  const { saving, error, run } = useSave()
  const urls = useRef<string[]>([])
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), [])

  const send = async () => {
    if (!text.trim() && photos.length === 0) return setMissing(true)
    const updated = await run(() => peticionesService.addComment(stay, request.id, { text: text.trim(), photos: photos.map((p) => p.file) }))
    if (updated) {
      setOpen(false)
      setText('')
      setPhotos([])
      setSent(true)
      setRequest(updated)
    }
  }

  return (
    <>
      <Card>
        <div className="ayuda__request-head">
          <h2 className="ayuda__request-title">{requestTitle(request, t)}</h2>
          <StatusChip tone={chip.tone} icon={chip.icon}>
            {t(`v2.ds.requestCard.${request.status}`)}
          </StatusChip>
        </div>
        <p className="ayuda__meta tnum">{meta}</p>
      </Card>
      <Card>
        <Timeline
          items={request.timeline.map((m) => ({
            id: m.code,
            state: m.state,
            title: t(`v2.peticiones.seguimiento.hito.${m.code}`),
            ...(m.date ? { date: dayMonthTime(m.date, lang) } : {}),
            ...(m.note ? { description: m.noteAuthor ? t('v2.peticiones.seguimiento.nota', { note: m.note, author: m.noteAuthor }) : m.note } : {}),
          }))}
        />
      </Card>

      {request.comments.length > 0 && (
        <section className="pagos__group" aria-labelledby="peticion-comentarios">
          <h2 id="peticion-comentarios" className="pagos__h2">
            {t('v2.peticiones.seguimiento.tusComentarios')}
          </h2>
          {request.comments.map((c) => (
            <Card key={c.id} className="ayuda__comment">
              {c.text && <p className="ayuda__text">{c.text}</p>}
              <span className="ayuda__meta tnum">{[dayMonthTime(c.sentAt, lang), c.photoCount > 0 ? t('v2.peticiones.seguimiento.fotos', { count: c.photoCount }) : null].filter(Boolean).join(' · ')}</span>
            </Card>
          ))}
        </section>
      )}
      {sent && <SuccessBlock title={t('v2.peticiones.seguimiento.comentarioEnviado')} headingLevel={2} />}

      {request.status !== 'resolved' && (
        <Button variant="secondary" onClick={() => setOpen(true)}>
          {t('v2.peticiones.seguimiento.comentar')}
        </Button>
      )}

      <Sheet open={open} title={t('v2.peticiones.seguimiento.comentar')} confirmLabel={t('v2.peticiones.seguimiento.enviarComentario')} onConfirm={() => void send()} onCancel={() => setOpen(false)} loading={saving}>
        <div className="pagos__stack">
          <Textarea
            label={t('v2.peticiones.seguimiento.comentario')}
            rows={3}
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setMissing(false)
            }}
            error={missing ? t('v2.peticiones.seguimiento.comentarioError') : undefined}
          />
          <PhotoAttach
            label={t('v2.peticiones.nueva.foto')}
            labelNote={t('v2.peticiones.nueva.fotoNota')}
            photos={photos.map((p, i) => ({ id: p.id, ...(p.url ? { url: p.url } : {}), label: t('v2.peticiones.nueva.fotoN', { n: i + 1 }) }))}
            onAdd={(file) => {
              const url = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : undefined
              if (url) urls.current.push(url)
              setPhotos((list) => [...list, { id: `${Date.now()}-${list.length}`, file, url }])
              setMissing(false)
            }}
            onRemove={(pid) => setPhotos((list) => list.filter((p) => p.id !== pid))}
          />
          {error && (
            <p className="pagos__error" role="alert">
              {error}
            </p>
          )}
        </div>
      </Sheet>
    </>
  )
}
